/**
 * The avatar menu — who you are, how the app looks, and the way out.
 *
 * The Admin Console is deliberately NOT here (the current SPA's TopBar puts it
 * in the avatar): in a Wallet-shaped app, "More" is the drawer of destinations
 * and the avatar is identity + appearance. Admin lives in the More surface with
 * every other destination, gated by exactly the same `isAdminOnlyPath` role
 * check it has always been gated by.
 *
 * The theme control is three-state, not a sun/moon flip, because `system` is
 * the stored default (`lib/theme.ts`) and a two-way toggle cannot express it —
 * flipping it once would silently pin a user who wanted to follow their OS.
 * `saveTheme` repaints immediately and persists to `rent.user_preference`, so
 * the choice follows the user to another browser.
 *
 * Hand-rolled dropdown (ref + `mousedown` + close on route change), the same
 * pattern as `NotificationBell` and the current TopBar — there is no popover
 * primitive in this repo and adding one is not this shell's job.
 */
import { useEffect, useRef, useState } from "react";
import Link from "@/compat/link";
import { usePathname } from "@/compat/navigation";
import { signOut } from "@/compat/next-auth-react";
import { LogOut, Monitor, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { saveTheme, useTheme, type ThemeMode } from "@/lib/theme";
import { AdminScopeToggle } from "@/components/layout/AdminScopeToggle";
import { PREFERENCES_ENTRY } from "./nav";

const MODES: { mode: ThemeMode; label: string; icon: typeof Sun }[] = [
  { mode: "light", label: "Light", icon: Sun },
  { mode: "dark", label: "Dark", icon: Moon },
  { mode: "system", label: "Auto", icon: Monitor },
];

export function AvatarMenu({
  name,
  role,
  placement = "down",
}: {
  name: string;
  role: string;
  /** `up` for a trigger that sits at the bottom of the screen (the desktop rail). */
  placement?: "down" | "up";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const { mode } = useTheme();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const initial = (name.trim().charAt(0) || "?").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-700 text-sm font-semibold text-white transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent)]"
      >
        {initial}
      </button>

      {open && (
        <div
          role="menu"
          className={
            placement === "up"
              ? "absolute bottom-full left-0 z-50 mb-2 w-64 overflow-hidden rounded-[var(--cp-r-card)] border border-[var(--cp-line)] bg-[var(--cp-surface)] py-1 shadow-[var(--cp-shadow-lg)]"
              : "absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-[var(--cp-r-card)] border border-[var(--cp-line)] bg-[var(--cp-surface)] py-1 shadow-[var(--cp-shadow-lg)]"
          }
        >
          <div className="border-b border-[var(--cp-line-2)] px-4 py-3">
            <p className="truncate text-sm font-semibold text-[var(--cp-ink)]">{name}</p>
            <span className={cn("cp-pill mt-2", role === "ADMIN" ? "cp-pill--prog" : "cp-pill--wait")}>
              {role}
            </span>
          </div>

          <div className="border-b border-[var(--cp-line-2)] px-3 py-2.5">
            <p className="cp-micro-label pb-1.5">Appearance</p>
            <div className="grid grid-cols-3 gap-1">
              {MODES.map((option) => {
                const active = option.mode === mode;
                return (
                  <button
                    key={option.mode}
                    type="button"
                    onClick={() => void saveTheme(option.mode)}
                    aria-pressed={active}
                    className={cn(
                      "flex flex-col items-center gap-1 rounded-[var(--cp-r-control)] px-2 py-2 text-[11px] font-medium transition-colors",
                      active
                        ? "bg-[var(--cp-accent-wash)] text-[var(--cp-accent-ink)]"
                        : "text-[var(--cp-ink-2)] hover:bg-[var(--cp-surface-2)]",
                    )}
                  >
                    <option.icon className="h-4 w-4" />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/*
            Admin-only "Just mine / All family" data-scope control. Reuses the
            exact `AdminScopeToggle` component + `useAdminScope` state the
            classic TopBar has always used — never a second implementation —
            so the two shells can never disagree about what "all family" means.
            It was previously mounted ONLY in the classic TopBar; the wallet
            shell mounted just the read-only `AdminScopeBanner`, which offered
            no way back to family-wide mode once turned off (2026-08-28
            admin-console audit, "7 of 9 accounts invisible").
          */}
          {role === "ADMIN" && (
            <div className="border-b border-[var(--cp-line-2)] px-3 py-2.5">
              <p className="cp-micro-label pb-1.5">Data scope</p>
              <AdminScopeToggle className="w-full" />
            </div>
          )}

          <Link
            href={PREFERENCES_ENTRY.href}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-[var(--cp-ink-2)] transition-colors hover:bg-[var(--cp-surface-2)] hover:text-[var(--cp-ink)]"
          >
            <PREFERENCES_ENTRY.icon className="h-4 w-4 text-[var(--cp-ink-3)]" />
            {PREFERENCES_ENTRY.label}
          </Link>


          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut({ callbackUrl: "/login" })}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-[var(--cp-rose-ink)] transition-colors hover:bg-[var(--cp-rose-wash)]"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
